import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { fromEvent, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalRepository, AnimalSearchFilters } from 'src/app/modules/animals/states/animal.repository';

@Directive({
  selector: '[appAnimalUniqueCheck]',
  standalone: true,
})
export class AnimalUniqueCheckDirective implements OnInit, OnDestroy {
  /**
   * Welcher Filter-Key soll benutzt werden? z.B. 'transponderNumber', 'bookNumber', ...
   */
  @Input('appAnimalUniqueCheck') filterKey!: keyof AnimalSearchFilters;

  /**
   * Ab welcher Eingabelänge soll geprüft werden?
   */
  @Input() minLength = 3;

  /**
   * Optional: ID des aktuellen Tiers, damit es aus der Duplikat-Liste gefiltert werden kann.
   */
  @Input() currentAnimalId: number | null = null;

  /**
   * Gibt alle gefundenen Tiere zurück, wenn welche existieren.
   */
  @Output() duplicateFound = new EventEmitter<AnimalModel[]>();

  private destroy$ = new Subject<void>();

  constructor(
    private elRef: ElementRef<HTMLInputElement>,
    private animalRepo: AnimalRepository
  ) {}

  ngOnInit(): void {
    const input = this.elRef.nativeElement;

    fromEvent<InputEvent>(input, 'input')
      .pipe(
        map(e => (e.target as HTMLInputElement).value.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (!this.filterKey || !value || value.length < this.minLength) {
            this.setDuplicateState(false);
            this.duplicateFound.emit([]);
            return of<AnimalModel[]>([]);
          }

          const filters: AnimalSearchFilters = {
            [this.filterKey]: value,
          } as any;

          return this.animalRepo
            .searchAnimalsWithFilters(filters, undefined)
            .pipe(catchError(() => of<AnimalModel[]>([])));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(results => {
        const filtered =
          this.currentAnimalId != null
            ? results.filter(a => a.id !== this.currentAnimalId)
            : results;

        const hasDuplicates = filtered.length > 0;
        this.setDuplicateState(hasDuplicates);
        this.duplicateFound.emit(filtered);
      });
  }

  private setDuplicateState(hasDuplicates: boolean): void {
    const input = this.elRef.nativeElement;

    if (hasDuplicates) {
      input.classList.add('border-yellow-400', 'bg-yellow-50');
      input.classList.add('animal-duplicate-warning');
    } else {
      input.classList.remove('border-yellow-400', 'bg-yellow-50');
      input.classList.remove('animal-duplicate-warning');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}